import React, { useState } from "react";
import { GridRow, GridRowProps } from "@mui/x-data-grid-pro";
import { GetAllFilesAndFoldersByUserDto } from "../../types/FolderPermission";
import { FolderFileType } from "../../data/Enums";
import { useDispatch } from "react-redux";
import { setDragRowFolder } from "../../redux/dragRowSlice";
import { MoveFolderDto } from "../../types/Folder";
import { AUDITRACEACTIONS, SEVERITIES } from "../../data/constants";
import { setRowSelectedFolder } from "../../redux/rowSelectedSlice";
import { useUser } from "../../context/UserContext";
import { useToaster } from "../../hooks/useToaster";
import {
  DragMessages,
  GLOBALMESSAGES,
  MOVEFILE_SUCCESSFUL,
  SOURCE_DONT_HAVE_PERMISSION,
} from "../../constants/messages";
import { logAuditTrace } from "../../services/auditNavigationTraceService";
import { getCurrentESTTime } from "../../utils/dateHelper";
import FileFolderExistPopup from "../folder/FileFolderExistPopup";
import { checkFolderOrFileExistWhileMove, deleteFolderOrFile } from "../../services/folderService";
import useApi from "../../hooks/useApi";

export interface CustomDraggableRowProps extends GridRowProps {
  moveFolderOrFile: (dto: MoveFolderDto) => Promise<boolean>;
}

const CustomDraggableRow: React.FC<CustomDraggableRowProps> = ({ moveFolderOrFile, ...props }) => {
  const { row } = props;
  const dispatch = useDispatch();
  const { userId } = useUser();
  const showToaster = useToaster();
  const targetRow = row as GetAllFilesAndFoldersByUserDto;
  const canEdit = targetRow?.canEdit;
  const [openFileReplacer, setOpenFileReplacer] = useState(false);
  const [currentRow, setCurrentRow] = useState<GetAllFilesAndFoldersByUserDto | null>(null);
  const { executeApi } = useApi();
  const [fileName, setFileName] = useState<string>();
  const [folderName, setFolderName] = useState<string>();
  const [type, setType] = useState<string>();
  const [pendingMoveDto, setPendingMoveDto] = useState<MoveFolderDto>();
  const [tempActionDescription, setTempActionDescription] = useState<string>("");
  const [rowType, setRowType] = useState<string>("");
  const [tempDraggedFolderId, setTempDraggedFolderId] = useState<number>();
  const [tempNewParentFolderId, setTempNewParentFolderId] = useState<number>();

  const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (!canEdit) {
      // Cancel the drag outright so the move can never reach the server, and
      // tell the user why instead of failing silently.
      event.preventDefault();
      showToaster(SOURCE_DONT_HAVE_PERMISSION(targetRow?.description), SEVERITIES.WARNING);
      return;
    }
    event.currentTarget.classList.add("dragging");
    const draggedItemId = targetRow.folderId?.toString() || targetRow.fileId?.toString();
    event.dataTransfer.setData("text/plain", draggedItemId);
    event.dataTransfer.setData("application/json", JSON.stringify(targetRow));
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {    
    event.preventDefault();
    event.currentTarget.classList.remove("dragging");
    const draggedData = event.dataTransfer.getData("application/json");
    if (!draggedData) return;

    const draggedRow = JSON.parse(draggedData) as GetAllFilesAndFoldersByUserDto;
    if (!draggedRow) return;

    if (
      (draggedRow.folderId && draggedRow.folderId === targetRow.folderId) ||
      (draggedRow.fileId && draggedRow.fileId === targetRow.fileId)
    ) {
      return;
    }

    if (targetRow.type === FolderFileType[FolderFileType.File]) {
      showToaster(DragMessages.InvalidDropTarget, SEVERITIES.WARNING);
      return;
    }

    const newParentFolderId = targetRow.folderId;
    setType(draggedRow?.type);
    if (draggedRow?.type === FolderFileType[FolderFileType.Folder]) {
      setTempDraggedFolderId(draggedRow.folderId);
      setTempNewParentFolderId(newParentFolderId);
      const moveFolderDto: MoveFolderDto = {
        folderFileType: FolderFileType.Folder,
        typeId: draggedRow.folderId,
        parentFolderId: newParentFolderId,
        userId: userId,
      };
      setPendingMoveDto(moveFolderDto);
      const isExist = await beforeMoveValidateFolder(draggedRow.folderId, newParentFolderId);
      setRowType(draggedRow?.type);
      if (isExist) {
        setFolderName(draggedRow?.description);
        setOpenFileReplacer(true);
      } else {
        dispatch(
          setDragRowFolder({
            draggedFolderId: draggedRow.folderId,
            parentFolderId: newParentFolderId,
          })
        );
      }
    } else {
      const moveFolderDto: MoveFolderDto = {
        folderFileType: FolderFileType.File,
        typeId: draggedRow.fileId,
        parentFolderId: newParentFolderId,
        userId: userId,
      };
      let actionDescription =
        draggedRow?.type === FolderFileType[FolderFileType.Folder]
          ? AUDITRACEACTIONS.MOVEDFOLDER.toString()
          : AUDITRACEACTIONS.MOVEDFILE.toString();

      setTempActionDescription(actionDescription);
      var exist = await executeApi<MoveFolderDto, boolean>(
        checkFolderOrFileExistWhileMove,
        moveFolderDto
      );
      setPendingMoveDto(moveFolderDto);
      if (exist) {
        setFileName(draggedRow.description);
        setCurrentRow(draggedRow);
        setOpenFileReplacer(true);
        return false;
      }

      const result = await moveFolderOrFile(moveFolderDto);
      if (result) {
        dispatch(setRowSelectedFolder({ parentFolderId: newParentFolderId }));
        showToaster(MOVEFILE_SUCCESSFUL(targetRow.description), SEVERITIES.SUCCESS);
      } else {
        actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
      }
      logAuditTrace(
        {
          userId: userId,
          objectType: draggedRow?.type,
          objectId:
            draggedRow?.type === FolderFileType[FolderFileType.Folder]
              ? draggedRow?.folderId
              : draggedRow?.fileId,
          objectName: draggedRow?.description,
          action: actionDescription,
          navigationDateTime: getCurrentESTTime(),
        },
        dispatch
      );
    }
  };
  const beforeMoveValidateFolder = async (draggedId: number, targetId: number | null) => {
    const moveFolderDto: MoveFolderDto = {
      folderFileType: FolderFileType.Folder,
      typeId: draggedId,
      parentFolderId: targetId,
      userId: userId,
    };
    var exist = await executeApi<MoveFolderDto, boolean>(
      checkFolderOrFileExistWhileMove,
      moveFolderDto
    );
    return exist;
  };
  const closeFileReplacerDialog = async () => {
    setOpenFileReplacer(false);
  };
  const restoreOrSkip = async (isSkip: boolean, row: GetAllFilesAndFoldersByUserDto) => {
    if (isSkip) {
      closeFileReplacer();
      return false;
    }
    // delete first and then move
    let currentMoveDataDto = pendingMoveDto;
    await executeApi<MoveFolderDto, boolean>(deleteFolderOrFile, currentMoveDataDto);
    if (rowType === FolderFileType[FolderFileType.Folder]) {
      dispatch(
        setDragRowFolder({
          draggedFolderId: tempDraggedFolderId,
          parentFolderId: tempNewParentFolderId,
        })
      );
    } else {
      //Move after delete
      const moveResult = await moveFolderOrFile(pendingMoveDto);
      let actionDescription = tempActionDescription;
      if (moveResult) {
        dispatch(setRowSelectedFolder({ parentFolderId: pendingMoveDto.parentFolderId }));
        showToaster(MOVEFILE_SUCCESSFUL(row.description), SEVERITIES.SUCCESS);
      } else {
        actionDescription += GLOBALMESSAGES.FAILED_ADDITION;
      }

      //Audit log
      logAuditTrace(
        {
          userId: userId,
          objectType: row?.type,
          objectId:
            row?.type === FolderFileType[FolderFileType.Folder] ? row?.folderId : row?.fileId,
          objectName: row?.description,
          action: actionDescription,
          navigationDateTime: getCurrentESTTime(),
        },
        dispatch
      );
    }
    closeFileReplacer();
  };
  const closeFileReplacer = async () => {
    setOpenFileReplacer(false);
  };
  return (
    <>
      <GridRow
        {...props}
         draggable
         onDragStart={handleDragStart}
         onDragOver={canEdit ? handleDragOver : undefined}
         onDrop={canEdit ? handleDrop : undefined}
      />
      <FileFolderExistPopup
        open={openFileReplacer}
        currentRow={currentRow}
        onClose={closeFileReplacerDialog}
        handleRestore={restoreOrSkip}
        fileName={fileName}
        folderName={folderName}
        type={type}
      />
    </>
  );
};

export default CustomDraggableRow;
